import { ILogger } from '@remento/logger';
import { Debugger } from '@remento/web-analytics-router/debugger';
import { Payload, Router } from '@remento/web-analytics-router/router';
import { EmailFormSubmitEvent, EmailFormSubmitPayload } from '@remento/web-analytics-router/sources/form/@types';

export function extractFormPayload(logger: ILogger, $form: HTMLDivElement): Payload<EmailFormSubmitPayload> | null {
  const formType = $form.dataset.analyticsFormType;
  if (formType == null) {
    logger.error('WEBFLOW_FORM_SUBMITTED', () => ({ error: 'NO_TYPE_ATTRIBUTE' }));
    return null;
  }
  const formListId = $form.dataset.analyticsListId;
  const $emailInput = $form.querySelector('[type|="email"]') as HTMLInputElement;
  if ($emailInput == null) {
    logger.error('WEBFLOW_FORM_SUBMITTED', () => ({ error: 'NO_EMAIL_INPUT' }));
    return null;
  }
  return {
    data: {
      email: $emailInput.value,
      formType: formType ?? 'unknown',
      formListId,
    },
    properties: {},
  };
}

export function registerWebflowEmailFormElementObserver(
  router: Router,
  logger: ILogger,
  $formContainer: HTMLDivElement,
) {
  if ($formContainer == null) {
    logger.warn('WEBFLOW_FORM', () => ({ error: 'NO_PARENT_ELEMENT' }));
    return;
  }
  $formContainer.classList.remove('w-form');
  const $submitButton = $formContainer.querySelector('[type="submit"]') as HTMLButtonElement;
  if ($submitButton == null) {
    logger.warn('WEBFLOW_FORM', () => ({ error: 'NO_SUBMIT_ELEMENT' }));
    return;
  }
  let $form: HTMLDivElement | HTMLFormElement = $formContainer.querySelector('.form-inputs') as HTMLDivElement;
  if ($form == null) {
    $form = $formContainer.querySelector('form') as HTMLFormElement;
    if ($form == null) {
      logger.warn('WEBFLOW_FORM', () => ({ error: 'NO_FORM_INPUT_DIV_ELEMENT' }));
      return;
    }
  }
  const $successDiv = $formContainer.querySelector('.w-form-done') as HTMLDivElement;
  if ($successDiv == null) {
    logger.warn('WEBFLOW_FORM', () => ({ error: 'NO_SUCCESS_DIV_ELEMENT' }));
    return;
  }
  const $errorDiv = $formContainer.querySelector('.w-form-fail') as HTMLDivElement;
  if ($errorDiv == null) {
    logger.warn('WEBFLOW_FORM', () => ({ error: 'NO_ERROR_DIV_ELEMENT' }));
    return;
  }
  $submitButton.addEventListener('click', async () => {
    $submitButton.disabled = true;
    $submitButton.value = $submitButton.attributes.getNamedItem('data-wait')?.value ?? 'Please wait...';
    try {
      const payload = extractFormPayload(logger, $formContainer);
      if (payload == null) {
        return null;
      }
      logger.debug(`WEBFLOW_FORM_SUBMITTED`);
      await router.publish(EmailFormSubmitEvent, payload);
      $form.style.display = 'none';
      $successDiv.style.display = 'block';
    } catch (err) {
      $form.style.display = 'none';
      $errorDiv.style.display = 'block';
    }
  });
}

export function registerWebflowEmailFormsObserver(router: Router, debug: Debugger | null) {
  if (!router.registerPublisher('email-form-webflow-observer')) {
    return;
  }
  const logger = router.logger.extend('form-observer-webflow');
  const $emailForms = document.querySelectorAll('[data-analytics-form-type]');
  for (let i = 0; i < $emailForms.length; i++) {
    registerWebflowEmailFormElementObserver(router, logger, $emailForms.item(i) as HTMLDivElement);
  }
  debug?.registerTooltips('email-form-webflow-observer', () => ({
    elements: Array.from($emailForms).flatMap(($emailForm) =>
      Array.from($emailForm.querySelectorAll('[type="submit"]') as NodeListOf<HTMLElement>),
    ),
    getEvent: ($element) => {
      const $form = $element as HTMLDivElement | null;
      const payload = $form == null ? null : extractFormPayload(logger, $form);
      return { type: 'website.form', payload, identify: payload?.data.email };
    },
  }));
}
